import moment from 'moment';
import { isNil } from 'lodash';
import { AuthState } from '@packages/@types/authContext';

import {
    AutoStackingConfigAttributes,
    ExternalApiForwarderAttributes,
    FeatureConfigAttributesBase,
    FleetsConfigAttributes,
    JsonTransformConfigAttributes,
    PriceMatrixAttributes,
    QuoteDenyingConfigAttributes,
    WebhookConfigAttributes,
} from '../../../../../server/src/models';
import { AutoStackingTimeSlot, FeaturePathMatching } from '../../../types/Feature';
import { Role } from '../../../types/Auth';

import { NewClientCredentials } from '../../../types/Client';
import { DriversEtaFleetCredential } from '../../../../../server/src/services/custom-features/drivers-eta/types/DriversEtaConfiguration';
import { DriversEtaAttributes } from '../../../../../server/src/models/featureConfigAttributes/DriversEtaAttributes';
import { SmartFleetChoiceAttributes } from '../../../../../server/src/models/featureConfigAttributes/SmartFleetChoiceAttributes';
import { FullEtaConfigAttributes } from '../../../../../server/src/models/featureConfigAttributes/FullEtaAttribues';
import { ClosingToolAttributes } from '../../../../../server/src/models/featureConfigAttributes/ClosingToolAttributes';
import { stringToLCAlphaNum } from '../../../../../server/src/utils/string';
import { PreparationTimeAttributes } from '../../../../../server/src/models/featureConfigAttributes/PreparationTimeAttributes';

export const extractAutostackingFormData = (formData: Record<string, any>): AutoStackingConfigAttributes => {
    const timeSlots = formData.timeSlots
        ? /* eslint-disable indent */
          formData.timeSlots.map((slot: AutoStackingTimeSlot) => {
              const startTime = moment(slot.startTime).utc().format('HH:mm:ssZ');
              const endTime = moment(slot.endTime).utc().format('HH:mm:ssZ');
              return { ...slot, startTime, endTime };
          })
        : [];

    return {
        enabled: formData.enabled,
        minTimeUntilPickup: formData.minTimeUntilPickup,
        maxDrivenDistance: formData.maxDrivenDistance || undefined,
        maxTotalTime: formData.maxTotalTime || undefined,
        packageTypeFilter: formData.packageTypeFilter,
        pickupAddressFilter: formData.pickupAddressFilter?.map((item: any) => item.pickupAddress) || [],
        maxStackSize: formData.maxStackSize,
        transportType: formData.transportType,
        notificationSlackChannel: formData.notificationSlackChannel,
        notificationSlackMessage: formData.notificationSlackMessage,
        timeSlots,
    };
};

export const extractWebhookFormData = (formData: Record<string, any>): WebhookConfigAttributes => {
    return {
        enabled: formData.webhook_enabled,
        redirectUrl: formData.redirectUrl,
        fetchPictureUrl: formData.fetchPictureUrl,
    };
};

export const extractAPIForwarderFormData = (formData: Record<string, any>): ExternalApiForwarderAttributes => {
    return {
        enabled: formData.apiforwarderEnabled,
        apiName: formData.apiforwarderApiName,
        baseUrl: formData.apiforwarderBaseurl,
        authUrl: formData.apiforwarderAuthUrl,
        login: formData.apiforwarderLogin,
        clientSecret: formData.apiforwarderClientSecret,
        password: formData.apiforwarderPassword,
        isFlex: formData.apiforwarderIsFlex,
        useAddressQualificator: formData.apiforwarderUseAddressQualificator,
        slackChannelId: formData.apiforwarderSlackChannelId,
    };
};

export const extractJsonFormData = (
    formData: Record<string, any>,
    user: AuthState['user'] | null,
): Array<{ data: JsonTransformConfigAttributes; featureId: number; id: number }> | undefined => {
    let jsonTransform: Array<{ data: JsonTransformConfigAttributes; featureId: number; id: number }> | undefined;

    if (user && [Role.StuartStaff, Role.SE].some((role) => user.role === role)) {
        jsonTransform = formData.jsonTransform?.map((jsonTransformData: any) => {
            const method = jsonTransformData?.jsonTransformHTTPMethod;
            let path: FeaturePathMatching | undefined;
            if (jsonTransformData?.jsonTransformPath) {
                path = {
                    value: jsonTransformData?.jsonTransformPath,
                    matching: jsonTransformData?.jsonPathMatchingMethod,
                };
            }
            return {
                data: {
                    enabled: jsonTransformData?.jsonTransformEnabled,
                    transformFunction: jsonTransformData?.jsonTransformFunction,
                    target: jsonTransformData?.jsonTransformTarget,
                    method: method === 'any' ? '' : method,
                    path,
                },
                featureId: jsonTransformData?.featureId,
                id: jsonTransformData?.id,
            };
        });
    }
    return jsonTransform;
};

export const extractCredentials = (formData: Record<string, any>): NewClientCredentials => {
    return {
        apiSecret: formData.apiSecret,
        v3email: formData.v3email,
        v3password: formData.v3password,
    };
};

export const extractFleetsFormData = (formData: Record<string, any>): FleetsConfigAttributes => {
    return {
        enabled: formData.fleetsEnabled,
        fleets:
            formData.fleets?.map((f: any) => ({
                id: f.id,
                dropOffServiceTime: f.dropOffServiceTime,
                maxCapacity: f.maxCapacity,
                name: f.name,
                pickUpServiceTime: f.pickUpServiceTime,
                type: f.type,
            })) || [],
    };
};

export const extractPriceMatrixFormData = (formData: Record<string, any>): PriceMatrixAttributes => {
    let pricePerItem;
    if (formData?.pricePerItem) {
        const numberOrUndefined = (num: string | number | undefined) =>
            (`${num}`?.length || 0) > 0 && !Number.isNaN(Number(num)) ? Number(num) : undefined;
        const pricePercentagePerAdditionalItem = numberOrUndefined(
            formData?.pricePerItem?.pricePercentagePerAdditionalItem,
        );
        const fixPricePerAdditionalItem = numberOrUndefined(formData?.pricePerItem?.fixPricePerAdditionalItem);
        const fixFeePerAdditionalItem = numberOrUndefined(formData?.pricePerItem?.fixFeePerAdditionalItem);
        const maxFixPriceItems = numberOrUndefined(formData?.pricePerItem?.maxFixPriceItems);
        pricePerItem = {
            pricePercentagePerAdditionalItem,
            fixFeePerAdditionalItem,
            fixPricePerAdditionalItem,
            maxFixPriceItems,
            enabled: formData.pricePerItem?.enabled || false,
            items: formData.pricePerItem?.items?.map(
                ({ name, price, nextDayPrice }: { name: string; price: string; nextDayPrice: string }) => ({
                    name,
                    sanitizedName: stringToLCAlphaNum(name),
                    price: Number(price),
                    nextDayPrice: Number(nextDayPrice),
                }),
            ),
        };
    }
    return {
        enabled: true,
        distanceFees: formData.distanceFees?.map(
            ({ kmFee, maxKm, minKm }: { kmFee: string | number; maxKm: string | number; minKm: string | number }) => ({
                minKm: Number(`${minKm}`.replace(',', '.')),
                maxKm: Number(`${maxKm}`.replace(',', '.')),
                kmFee: Number(`${kmFee}`.replace(',', '.')),
            }),
        ),
        dateFees: formData.dateFees?.map(
            ({
                applyMethod,
                dayOfTheWeek,
                unit,
                value,
            }: {
                applyMethod: string;
                dayOfTheWeek: number;
                unit: string;
                value: number;
            }) => ({
                applyMethod,
                dayOfTheWeek: Number(`${dayOfTheWeek}`.replace(',', '.')),
                unit,
                value: Number(`${value}`.replace(',', '.')),
            }),
        ),
        serviceFees: formData.serviceFees?.map(
            ({
                code,
                price,
                forWholeDelivery,
            }: {
                code: string;
                price: number | string;
                forWholeDelivery: boolean;
            }) => ({
                code,
                forWholeDelivery,
                price: Number(`${price}`.replace(',', '.')),
            }),
        ),
        pricePerItem,
        priceMatrix:
            formData.priceMatrix?.map(({ type, prices }: { type: string; prices: any }) => ({
                type,
                prices: prices.map(
                    ({
                        maxWeight,
                        maxLength,
                        price,
                        nextDayPrice,
                    }: {
                        maxWeight: string | number;
                        maxLength: string | number;
                        price: string | number;
                        nextDayPrice: string | number;
                    }) => ({
                        maxWeight: Number(`${maxWeight}`.replace(',', '.')),
                        maxLength:
                            isNil(maxLength) || maxLength === '' ? undefined : Number(`${maxLength}`.replace(',', '.')),
                        price: Number(`${price}`.replace(',', '.')),
                        nextDayPrice: Number(`${nextDayPrice}`.replace(',', '.')),
                    }),
                ),
            })) || [],
        taxValue: formData.taxValue,
    };
};

export const extractDriversEtaFormData = (formData: Record<string, any>): DriversEtaAttributes => {
    const data: { [key: string]: DriversEtaFleetCredential } = {};

    formData.driversEtas?.forEach(
        ({ fleetId, ssoUsername, ssoPassword }: { fleetId: number; ssoUsername: string; ssoPassword: string }) => {
            data[`${fleetId}`] = { ssoUsername, ssoPassword };
        },
    );

    return {
        enabled: formData.driversEtaEnabled,
        ...data,
    };
};

export const extractFullEtaFormData = (formData: Record<string, any>): FullEtaConfigAttributes => {
    return {
        enabled: formData.fullEtaEnabled,
        errorMargin: formData.errorMargin,
        minimumValue: formData.minimumValue,
        delta: formData.delta,
        shouldCallClosingTool: formData.shouldCallClosingTool,
    };
};

export const extractPreparationFormData = (formData: Record<string, any>): PreparationTimeAttributes => {
    return {
        enabled: formData.preparationTimeEnabled,
        prepNowEnabled: formData.prepNowEnabled,
    };
};

export const extractClosingToolFormData = (formData: Record<string, any>): ClosingToolAttributes => {
    return {
        enabled: formData.enabled,
        createJobEnabled: formData.createJobEnabled,
        validationEnabled: formData.validationEnabled,
        etaEnabled: formData.etaEnabled,
        pricingEnabled: formData.pricingEnabled,
        clientCode: formData.clientCode,
    };
};

export const extractSmartFleetChoiceFormData = (formData: Record<string, any>): SmartFleetChoiceAttributes => {
    return {
        enabled: formData.sfcEnabled,
        customJSLogic: formData.sfcFunction,
        fleets: formData.sfcFleets
            ? formData.sfcFleets.map(
                  ({ sfcFleetType: type, sfcFleetId: id }: { sfcFleetId: number; sfcFleetType: string }) => ({
                      id,
                      type,
                  }),
              )
            : undefined,
    };
};

export const extractQuoteDenyingFormData = (formData: Record<string, any>): QuoteDenyingConfigAttributes => {
    return {
        enabled: formData.quoteDenyingEnabled,
        closingSchedules:
            formData.closingSchedule?.map((f: any) => ({
                start: f.start,
                end: f.end,
                repeat: f.repeat,
            })) || [],
        timeOfLastOrder: formData.nextDayCutOff?.timeOfLastOrder,
        reopeningTime: formData.nextDayCutOff?.reopeningTime,
        maxWeight: formData.packageMaxDimensions?.maxWeight,
        vehicleLimitations: formData.vehicleLimitations,
        cutOffAfterMidnight: formData.nextDayCutOff?.cutOffAfterMidnight,
        bypassStuartAPIValidation: formData.bypassStuartAPIValidation,
        sendSlackAlert: formData.sendSlackAlert,
        sameDayCutoff: formData.sameDayCutoff,
        // maxLength: number;
        // maxWidth: number;
        // maxHeight: number;
    };
};
